import { Link } from 'react-router-dom';
import StatusBlock from '@/components/base/StatusBlock';
import { useAsyncData } from '@/hooks/useAsyncData';
import { getAllArticles, formatViews, TYPE_LABEL } from '@/lib/content';
import type { Article } from '@/types/content';

export default function HotPlots() {
  const { data, loading, error, reload } = useAsyncData<Article[]>(
    () =>
      getAllArticles()
        .filter((a) => a.type === 'plot')
        .sort((a, b) => b.views - a.views)
        .slice(0, 3),
    [],
  );

  return (
    <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
            HOT STORYLINE
          </span>
          <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">热门剧情推荐</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            精选《疯狂试爱2》最受关注的剧情解读，点击卡片即可进入完整详情。
          </p>
        </div>
        <Link
          to="/category/plot"
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
        >
          浏览全部剧情
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      {loading && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-72 animate-pulse rounded-lg bg-background-100"></div>
          ))}
        </div>
      )}

      {!loading && error && (
        <StatusBlock icon="ri-wifi-off-line" title="网络异常" description={error} actionLabel="重新加载" onAction={reload} />
      )}

      {!loading && !error && data && data.length === 0 && (
        <StatusBlock icon="ri-inbox-archive-line" title="暂无相关内容" />
      )}

      {!loading && !error && data && data.length > 0 && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {data.map((article, index) => (
            <Link
              key={article.id}
              to={`/detail/${article.id}`}
              className="group relative flex h-72 flex-col justify-end overflow-hidden rounded-lg"
            >
              <img
                src={article.cover}
                alt={article.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-transparent"></div>
              <div className="relative z-10 p-5">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 font-heading text-sm font-bold text-background-50">
                    {index + 1}
                  </span>
                  <span className="rounded bg-background-50/20 px-2 py-0.5 text-xs font-medium text-background-50">
                    {TYPE_LABEL[article.type]}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-background-50">
                  {article.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm text-primary-100">{article.summary}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary-200">
                  <i className="ri-fire-line text-accent-400"></i>
                  {formatViews(article.views)} 阅读
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}